/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backgrid from 'backgrid';

import FileUploadModel from '../../fileupload/FileUploadModel';
import FileUploadCollection from '../../fileupload/FileUploadCollection';
import FileUploadView from '../../views/FileUploadView';

const FileUploadCell = Backgrid.Cell.extend({
  className: 'file-uploader-cell',

  // original properties from psa FileUploadCell
  metadata: {},
  isReadOnly: false,
  allowMultipleFiles: false,
  searchPath: undefined,
  pathToFileId: undefined,
  uploadXml: undefined,
  downloadXml: undefined,
  validator: undefined,
  onFileRemove: undefined,
  noAttachmentsRenderable: true,
  noAttachmentsPlaceholder: undefined,

  // new properties added for DoBS
  uploadSingleLabel: 'Attach File...',
  uploadMultipleLabel: 'Attach Files...',
  showError: false,
  btnClass: 'btn-primary',
  returnData() {},

  initialize(args) {
    this.column = args.column;
    const cellVal = args.model.get(this.column.get('name'));
    const readOnly = Backgrid.callByNeed(this.isReadOnly, this.column, this.model);

    if (cellVal) {
      this.fileCollection = new FileUploadCollection();

      // array of files
      if (_.isArray(cellVal)) {
        cellVal.forEach((file) => {
          this.fileCollection.add(
            new FileUploadModel({
              uuid: file.uuid,
              fileId: cellVal.fileId,
              fileName: file.fileName,
              isReadOnly: readOnly ? readOnly : false,
            })
          );
        });
      }
      // single file
      else {
        this.fileCollection.add(
          new FileUploadModel({
            uuid: cellVal.uuid,
            fileId: cellVal.fileId,
            fileName: cellVal.fileName,
            isReadOnly: readOnly ? readOnly : false,
          })
        );
      }
      // bug in backbone model.set where the objects are merged rather than replaced
      args.model.unset(this.column.get('name'));
      args.model.set(this.column.get('name'), _.pluck(this.fileCollection.models, 'attributes'));
    }

    const metadata = Backgrid.callByNeed(this.metadata, this.column, args.model);

    this.fileUploaderCell = new FileUploadView({
      el: this.$el,
      isReadOnly: readOnly,
      collection: this.fileCollection,
      metadata,
      allowMultipleFiles: this.allowMultipleFiles,
      uploadSingleLabel: this.uploadSingleLabel,
      uploadMultipleLabel: this.uploadMultipleLabel,
      showError: this.showError,
      btnClass: this.btnClass,
      validator: this.validator,
      onFileRemove: this.onFileRemove,
      returnData: this.returnData,
      childViewEvents: {
        render() {
          this.collection.bind('update', () => {
            // bug in backbone model.set where the objects are merged rather than replaced
            args.model.unset(args.column.get('name'));
            args.model.set(args.column.get('name'), _.pluck(this.models, 'attributes'));
          });
        },
      },
    });
  },

  render(params) {
    this.$el.empty();

    this.$el.addClass(this.column.get('name'));

    const readOnly = Backgrid.callByNeed(this.isReadOnly, this.column, this.model);
    if (!readOnly) {
      this.$el.addClass('editable');
    }

    // This enables the user to render a blank cell with optional placeholder text
    if (!this.noAttachmentsRenderable && !this.fileCollection) {
      this.$el.text(this.noAttachmentsPlaceholder);
      return this;
    }

    this.fileUploaderCell.render();

    this.delegateEvents();
    return this;
  },

  enterEditMode() {
    //no edit mode for this cell type
    return;
  },
});

export default FileUploadCell;
